import { z } from 'zod';
import { CONTACT_METHODS } from '@shared/constants';
import { optionalEmail, optionalText, paginationSchema, phoneSchema } from './common';

const customerBase = z.object({
  firstName: z.string().default(''),
  lastName: z.string().default(''),
  companyName: optionalText,
  phone: phoneSchema,
  email: optionalEmail,
  preferredContactMethod: z.enum(CONTACT_METHODS).optional(),
  billingAddressLine1: optionalText,
  billingAddressLine2: optionalText,
  billingCity: optionalText,
  billingState: optionalText,
  billingPostalCode: optionalText,
  notes: optionalText,
});

/**
 * A NAME IS NOT REQUIRED, and neither is an email. Operator decision,
 * 2026-07-31: real customers turn up identified only by a phone number or an
 * address, and the previous rule ("firstName || lastName || companyName")
 * refused to record them at all.
 *
 * What is still refused is a record with NOTHING in it, which is a mis-click
 * rather than a customer. That is deliberately a much weaker rule than the one
 * it replaces: a phone number alone satisfies it, or a billing address alone.
 * Relax it by deleting the refine if even that proves too strict.
 */
const IDENTIFYING_FIELDS = [
  'firstName',
  'lastName',
  'companyName',
  'phone',
  'email',
  'billingAddressLine1',
  'billingCity',
  'notes',
] as const;

export const createCustomerSchema = customerBase.refine(
  (data) =>
    IDENTIFYING_FIELDS.some((field) => {
      const value = data[field];
      return typeof value === 'string' && value.trim() !== '';
    }),
  {
    message: 'Enter at least one detail — a name, a phone number, or an address',
    path: ['firstName'],
  }
);

export const updateCustomerSchema = customerBase
  .partial()
  .refine((data) => Object.keys(data).length > 0, { message: 'Nothing to update' });

export const customerSearchSchema = paginationSchema.extend({
  q: optionalText,
  includeInactive: z.boolean().optional(),
});

export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;
export type CustomerSearchInput = z.infer<typeof customerSearchSchema>;
